import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";
import { LayerScope, keepScrollInLayer, useRaisedLayer } from "./layer";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

type PopoverContentProps = React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>;

/** Stacks above the dialog it is opened from (`useRaisedLayer`). */
const PopoverLayer = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = "center",
      sideOffset = 4,
      style,
      onWheel,
      onTouchMove,
      children,
      ...props
    },
    forwardedRef
  ) => {
    const layer = useRaisedLayer(className, style, forwardedRef);
    return (
      <PopoverPrimitive.Content
        ref={layer.ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 w-72 overscroll-contain rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 [&_*]:overscroll-contain",
          className
        )}
        style={layer.style}
        onWheel={keepScrollInLayer(onWheel)}
        onTouchMove={keepScrollInLayer(onTouchMove)}
        {...props}
      >
        <LayerScope z={layer.z}>{children}</LayerScope>
      </PopoverPrimitive.Content>
    );
  }
);
PopoverLayer.displayName = "PopoverLayer";

/**
 * Always portalled. Rendering a popover inline inside a dialog (the old
 * `portal={false}` workaround for the scroll lock) puts it inside the dialog's
 * transformed, `overflow-y-auto` box, which clips it at the dialog's edge — the
 * bottom options of a long list could not be reached.
 */
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>((props, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverLayer ref={ref} {...props} />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverAnchor = PopoverPrimitive.Anchor;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
